/**
 * Drawer component for sidebar navigation in the application
 * Manages chat history, favorites, and provides conversation management
 */
import React, { useEffect, useState } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import ChatItem from '../chatItem';
import { ChatItemType } from '../../interfaces/api/qna';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BiPlus } from 'react-icons/bi';
import { BsArrowUp } from 'react-icons/bs';
import {
  deleteChat,
  getChatHistory,
  getMessagesHistory,
  shareChat,
  toggleChatFavorite,
} from '../../screens/content/chat/actions';
import * as process from 'node:process';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from '../../redux/store';
import {
  removeChat,
  selectChat,
  setChats,
  setMessages,
  toggleChatFavoriteAction,
  setFavChats,
  appendChats,
  appendFavChats,
} from '../../screens/content/chat/reducer';
import { showGlobalModal } from '../../connected-components/globalModal/actions';
import { useNavigate } from 'react-router-dom';
import { exportChatToPdf } from '../../utils/pdf';
import { Message } from '../../interfaces/components/chatList';
import { toast } from 'sonner';
import { finishLoading, startLoading } from '../../connected-components/loading/actions';
import { IoSettingsOutline } from 'react-icons/io5';
import { openPreferences } from '../../connected-components/preferences/actions';
import HamburgerButton from '../hamburgerButton';
import { getLocalData } from '../../utils/storage';
import { keys } from '../../api/keys';
import { services } from '../../api';

// Helper functions for grouping by date

/**
 * Checks if a date is today
 * @param date - Date to check
 * @returns Boolean indicating if the date is today
 */
function isToday(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
  );
}

/**
 * Checks if a date is yesterday
 * @param date - Date to check
 * @returns Boolean indicating if the date is yesterday
 */
function isYesterday(date: Date): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()
  );
}

/**
 * Groups chats by date categories (Today, Yesterday, Earlier)
 * @param chats - Array of chat items to group
 * @returns Object with date categories as keys and arrays of chats as values
 */
function groupChatsByDate(chats: ChatItemType[]) {
  return chats.reduce((groups: { [key: string]: ChatItemType[] }, chat) => {
    const chatDate = new Date(chat.created_at);
    let groupName = 'Earlier';
    if (isToday(chatDate)) {
      groupName = 'Today';
    } else if (isYesterday(chatDate)) {
      groupName = 'Yesterday';
    }
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(chat);
    return groups;
  }, {});
}

/**
 * Shimmer animation keyframes for loading skeletons
 */
const shimmer = keyframes`
    0% {
        background-position: -1000px 0;
    }
    100% {
        background-position: 1000px 0;
    }
`;

/**
 * Shimmer animation keyframes for loading text
 */
const textShimmer = keyframes`
    0% {
        background-position: -100px 0;
    }
    100% {
        background-position: 100px 0;
    }
`;

/**
 * Container for skeleton loading items
 */
const SkeletonWrapper = styled.div`
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

/**
 * Individual skeleton item with customizable dimensions
 */
const SkeletonItem = styled.div<{ $width?: string; $height?: string }>`
  border-radius: 8px;
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '16px'};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.text.light + '11'} 25%,
    ${({ theme }) => theme.text.light + '22'} 37%,
    ${({ theme }) => theme.text.light + '11'} 63%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 5s linear infinite;
`;

/**
 * Animated loading text with gradient effect
 */
const LoadingText = styled.div`
  font-size: ${({ theme }) => theme.text.s9};
  font-weight: bold;
  font-family: Arial;
  background: linear-gradient(-90deg, #7a7a7a 0%, ${({ theme }) => theme.text.shine} 50%, #7a7a7a 100%);
  background-size: 200% auto;
  animation: ${textShimmer} 2s linear infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0.8;
`;

/**
 * Main sidebar container with responsive behavior
 */
const SidebarSection = styled.section<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  height: 100vh;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.sidebar.background};
  transition: all 0.3s ease;
  overflow: hidden;
  z-index: 100;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 260px;
    transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
  }

  ${({ $isOpen, theme }) =>
    !$isOpen &&
    `
      width: 0;
      padding: 0;
      opacity: 0;
      box-shadow: 0px 0px 14px ${theme.sidebar.shadow};
      filter: blur(14px);
  `}
`;

/**
 * Back button for mobile navigation
 */
const BackButton = styled.div`
  width: 90%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  color: ${({ theme }) => theme.text.light + 'EE'};
  background: ${({ theme }) => theme.text.light + '11'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  margin: 0 auto 0.5rem;

  &:hover {
    background: ${({ theme }) => theme.text.light + '22'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  button {
    border: none;
    background-color: unset;
    cursor: pointer;
    color: ${({ theme }) => theme.text.light + 'EE'};
    font-weight: 500;
  }

  svg {
    transform: rotate(-90deg);
  }
`;

/**
 * Header for the sidebar with new chat button
 */
const SidebarHeader = styled.div`
  width: 90%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  color: ${({ theme }) => theme.text.light + 'EE'};
  background: ${({ theme }) => theme.text.light + '11'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  margin: 0 auto;

  &:hover {
    background: ${({ theme }) => theme.text.light + '22'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  button {
    border: none;
    background-color: unset;
    cursor: pointer;
    color: ${({ theme }) => theme.text.light + 'EE'};
    font-weight: 500;
  }
`;

/**
 * Tab bar for switching between chats and favorites
 */
const TabBar = styled.div`
  width: 100%;
  display: flex;
  margin-top: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.text.light + '22'};
`;

/**
 * Tab button with active state styling
 */
const TabButton = styled.button<{ active?: boolean }>`
  flex: 1;
  padding: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: ${({ active }) => (active ? '700' : '500')};
  color: ${({ theme, active }) => (active ? theme.text.light : theme.text.light + '99')};
  border-bottom: ${({ active, theme }) => (active ? `2px solid ${theme.text.light}` : 'none')};

  &:hover {
    background: ${({ theme }) => theme.text.light + '11'};
  }
`;

/**
 * Container for chat history items
 */
const SidebarHistory = styled.div`
  width: 100%;
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 5%;
`;

const HistoryList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.text.light + '99'};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
`;

const EmptyMessages = styled.div`
  text-align: center;
  padding: 1rem;
  color: ${({ theme }) => theme.text.light};
  font-size: ${({ theme }) => theme.text.s8};
`;

const DrawerInfo = styled.div`
  width: 100%;
  margin: 0;
  padding: 1rem 5%;
  border-top: 1px solid ${({ theme }) => theme.text.light}22;
`;

const SettingsButton = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  color: ${({ theme }) => theme.text.light + 'EE'};
  background: ${({ theme }) => theme.text.light + '11'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.text.light + '22'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  span {
    font-size: 14px;
    font-weight: 500;
  }

  svg {
    font-size: 1.25rem;
  }
`;

// Add backdrop for mobile drawer
const Backdrop = styled.div<{ $isVisible: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};

  @media (max-width: 768px) {
    display: block;
  }
`;

interface DrawerProps {
  isVisible: boolean;
  onCreateNewChatPressed: () => void;
  onChatItemPressed: (chat: ChatItemType) => Promise<void>;
  onLogoutPressed: () => void;
  selectedChatId: string | number;
  isLoading: boolean;
  onBackPressed?: () => void;
}

const Drawer: React.FC<DrawerProps> = ({
  onCreateNewChatPressed,
  onChatItemPressed,
  selectedChatId,
  onBackPressed,
}) => {
  const dispatch = useDispatch();
  const { chats, favChats, messages, tab_id } = useSelector((state: RootState) => state.chat);
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'favorite'>('all');
  const [drawerOpen, setDrawerOpen] = useState(window.innerWidth > 768);

  // Pagination states for All Chats
  const [allPage, setAllPage] = useState(1);
  const [allHasMore, setAllHasMore] = useState(true);
  const [allLoading, setAllLoading] = useState(false);

  // Pagination states for Favorite Chats
  const [favPage, setFavPage] = useState(1);
  const [favHasMore, setFavHasMore] = useState(true);
  const [favLoading, setFavLoading] = useState(false);

  // Add a state to track if we're in the initial loading phase
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Handle resize event to show/hide drawer based on screen size
  useEffect(() => {
    const handleResize = () => {
      // On desktop, drawer should always be open by default
      if (window.innerWidth > 768) {
        setDrawerOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Close drawer on mobile when user clicks outside
  const handleBackdropClick = () => {
    if (window.innerWidth <= 768) {
      setDrawerOpen(false);
    }
  };

  // Reset scroll position and data on tab switch
  useEffect(() => {
    const container = document.getElementById('sidebar-scroll');
    if (container) {
      container.scrollTop = 0;
    }
  }, [activeTab]);

  // Fetch All Chats when activeTab is 'all'
  useEffect(() => {
    if (activeTab !== 'all' || !allHasMore || !tab_id) return;
    if (allPage === 1 && chats.length > 0) {
      setIsInitialLoading(false);
      return;
    }
    if (allPage > 1 && chats.length > (allPage - 1) * 50) return;

    const loadAllChats = async () => {
      setAllLoading(true);
      dispatch(setChats([])); // Clear chats before loading new ones
      const start = (allPage - 1) * 50;
      try {
        const res = await getChatHistory(start, 50, '', 0, tab_id);
        if (res.success) {
          const newChats = res.chats;
          if (newChats.length < 50) {
            setAllHasMore(false);
          }
          if (allPage === 1) {
            dispatch(setChats(newChats));
          } else {
            dispatch(appendChats(newChats));
          }
        }
      } catch (err) {
        if (process.env.REACT_APP_DEBUG === 'true') {
          console.error(err);
        }
      } finally {
        setAllLoading(false);
        setIsInitialLoading(false);
      }
    };
    loadAllChats();
  }, [activeTab, allPage, dispatch, allHasMore, tab_id, isInitialLoading]);

  // Fetch Favorite Chats when activeTab is 'favorite'
  useEffect(() => {
    if (activeTab !== 'favorite' || !tab_id) return;

    // If this is the first load and we switch to favorites, set initial loading to false
    if (isInitialLoading && favChats.length > 0) {
      setIsInitialLoading(false);
      return;
    }

    // If we don't have enough favorites for the current page, load them.
    if (favChats.length < favPage * 50 && favHasMore) {
      const loadFavChats = async () => {
        setFavLoading(true);
        const start = (favPage - 1) * 50;
        try {
          const res = await getChatHistory(start, 50, '', 1, tab_id);
          if (res.success) {
            const newChats = res.chats;
            if (newChats.length < 50) {
              setFavHasMore(false);
            }
            if (favPage === 1) {
              dispatch(setFavChats(newChats));
            } else {
              dispatch(appendFavChats(newChats));
            }
            setIsInitialLoading(false);
          }
        } catch (err) {
          if (process.env.REACT_APP_DEBUG === 'true') {
            console.error(err);
          }
          setIsInitialLoading(false);
        } finally {
          setFavLoading(false);
        }
      };
      loadFavChats();
    }
  }, [activeTab, favPage, dispatch, favChats.length, favHasMore, isInitialLoading]);

  // Determine current list based on activeTab
  const currentChats = activeTab === 'all' ? chats : favChats;
  const currentHasMore = activeTab === 'all' ? allHasMore : favHasMore;
  const currentLoading = activeTab === 'all' ? allLoading : favLoading;

  const fetchMoreData = () => {
    if (activeTab === 'all') {
      setAllPage((prev) => prev + 1);
    } else {
      setFavPage((prev) => prev + 1);
    }
  };

  // Toggle favorite status using Redux
  const handleToggleFavorite = async (chat: ChatItemType) => {
    const newFavoriteValue = +chat.in_favorite !== 1 ? 1 : 0;
    try {
      const res = await toggleChatFavorite(chat.id, newFavoriteValue === 1, tab_id);
      if (res) {
        dispatch(
          toggleChatFavoriteAction({
            id: chat.id,
            isFavorite: newFavoriteValue === 1,
          }),
        );
      }
    } catch (err) {
      if (process.env.REACT_APP_DEBUG === 'true') {
        console.error('Error in handleToggleFavorite:', err);
      }
    }
  };

  // Delete chat with confirmation modal
  const onChatDeleted = (chatId: number) => {
    showGlobalModal({
      title: 'Delete Chat',
      message: 'Are you sure you want to delete this chat? This action cannot be undone.',
      onConfirm: async () => {
        try {
          const response = await deleteChat(chatId);
          if (response) {
            dispatch(removeChat(chatId));
            store.dispatch(setMessages({ messages: [] }));
            store.dispatch(selectChat(null));
            navigate('/chat');
          }
        } catch (error) {
          if (process.env.REACT_APP_DEBUG === 'true') {
            console.error('Error deleting chat:', error);
          }
        }
      },
      type: 'question',
    });
  };

  const exportChat = async (id: number) => {
    try {
      startLoading();
      // Check if we're exporting the selected chat
      if (+selectedChatId === +id) {
        // Get the currently selected category to determine if it's RTL
        const selectedChatCategories = await getLocalData(keys.CHAT_CATEGORIES);
        const categoryId = selectedChatCategories?.[id];
        let isRTL = false;

        // If we have a category ID, check if it's an RTL category
        if (categoryId) {
          try {
            // Get the tab ID
            const tabId = tab_id || localStorage.getItem('active_tab_id');
            if (tabId) {
              // Get categories for this tab
              const tabCategories = await services.tabs.getCategories(parseInt(tabId));
              // Find the selected category
              const selectedCategory = tabCategories.find((cat) => cat.id === parseInt(categoryId));
              // Check if it's RTL
              isRTL = selectedCategory?.layout === 'rtl';
            }
          } catch (error) {
            console.error('Error checking RTL layout:', error);
          }
        }

        await exportChatToPdf(messages);
        return;
      }

      const res = await getMessagesHistory(id);
      if (!res.messages.length) {
        toast.error('No messages available for export.');
        return;
      }

      // Check if the chat has an associated category with RTL layout
      const selectedChatCategories = await getLocalData(keys.CHAT_CATEGORIES);
      const categoryId = selectedChatCategories?.[id];
      let isRTL = false;

      // If we have a category ID, check if it's an RTL category
      if (categoryId) {
        try {
          // Get the tab ID
          const tabId = tab_id || localStorage.getItem('active_tab_id');
          if (tabId) {
            // Get categories for this tab
            const tabCategories = await services.tabs.getCategories(parseInt(tabId));
            // Find the selected category
            const selectedCategory = tabCategories.find((cat) => cat.id === parseInt(categoryId));
            // Check if it's RTL
            isRTL = selectedCategory?.layout === 'rtl';
          }
        } catch (error) {
          console.error('Error checking RTL layout:', error);
        }
      }

      await exportChatToPdf(res.messages as Message[]);
    } catch (ex) {
      console.error('Export error:', ex);
      toast.error('An error occurred while exporting the chat. Please try again later.');
    } finally {
      finishLoading();
    }
  };

  return (
    <>
      <HamburgerButton isOpen={drawerOpen} onClick={toggleDrawer} />
      <Backdrop $isVisible={drawerOpen} onClick={handleBackdropClick} />
      <SidebarSection $isOpen={drawerOpen}>
        {onBackPressed && (
          <BackButton onClick={onBackPressed} aria-label="Go back to tabs">
            <BsArrowUp size={20} />
            <button>Back to Tabs</button>
          </BackButton>
        )}
        <SidebarHeader onClick={onCreateNewChatPressed}>
          <BiPlus size={20} />
          <button>New Chat</button>
        </SidebarHeader>

        <TabBar>
          <TabButton active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
            All Chats
          </TabButton>
          <TabButton active={activeTab === 'favorite'} onClick={() => setActiveTab('favorite')}>
            Favorite
          </TabButton>
        </TabBar>

        <SidebarHistory id="sidebar-scroll">
          {(isInitialLoading && currentChats.length === 0) || (currentLoading && currentChats.length === 0) ? (
            <SkeletonWrapper>
              <SkeletonItem $height="40px" style={{ marginTop: '2%' }} />
              <SkeletonItem $height="40px" style={{ marginTop: '2%' }} />
              <SkeletonItem $height="40px" style={{ marginTop: '2%' }} />
            </SkeletonWrapper>
          ) : !currentLoading && currentChats.length === 0 ? (
            <EmptyMessages>No chats available</EmptyMessages>
          ) : (
            <InfiniteScroll
              dataLength={currentChats.length}
              next={fetchMoreData}
              hasMore={currentHasMore}
              loader={
                <LoaderWrapper>
                  <LoadingText>Loading</LoadingText>
                </LoaderWrapper>
              }
              scrollableTarget="sidebar-scroll"
            >
              {activeTab === 'all' ? (
                Object.entries(groupChatsByDate(currentChats))
                  .sort((a, b) => {
                    const order = { Today: 1, Yesterday: 2, Earlier: 3 };
                    return order[a[0]] - order[b[0]];
                  })
                  .map(([group, chats]) =>
                    chats && chats.length > 0 ? (
                      <div key={group}>
                        <h4 style={{ color: theme.text.light, fontWeight: '600' }}>{group}</h4>
                        <HistoryList>
                          {chats.map((chat) => (
                            <ChatItem
                              key={chat.id}
                              id={chat.id}
                              title={chat.title || 'Untitled'}
                              isSelected={+chat.id === +selectedChatId}
                              isFavorite={chat.in_favorite}
                              onClick={() => onChatItemPressed(chat)}
                              onFavourite={() => handleToggleFavorite(chat)}
                              onShare={() => shareChat(chat.id?.toString())}
                              onExport={() => exportChat(chat.id)}
                              onDelete={() => onChatDeleted(chat.id)}
                            />
                          ))}
                        </HistoryList>
                      </div>
                    ) : null,
                  )
              ) : (
                <HistoryList>
                  {currentChats.map((chat) => (
                    <ChatItem
                      key={chat.id}
                      id={chat.id}
                      title={chat.title || 'Untitled'}
                      isSelected={+chat.id === +selectedChatId}
                      isFavorite={chat.in_favorite}
                      onClick={() => onChatItemPressed(chat)}
                      onFavourite={() => handleToggleFavorite(chat)}
                      onShare={() => shareChat(chat.id?.toString())}
                      onExport={() => exportChat(chat.id)}
                      onDelete={() => onChatDeleted(chat.id)}
                    />
                  ))}
                </HistoryList>
              )}
            </InfiniteScroll>
          )}
        </SidebarHistory>

        <DrawerInfo>
          <SettingsButton onClick={() => openPreferences()}>
            <IoSettingsOutline size={20} />
            <span>Preferences</span>
          </SettingsButton>
        </DrawerInfo>
      </SidebarSection>
    </>
  );
};

export default Drawer;
